import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/PageHeader";
import { formatDOP, formatNum } from "@/lib/format";
import {
  ArrowUpRight, ArrowDownRight, AlertTriangle, AlertOctagon, Info,
  TrendingUp, Package, Receipt, Users, Wallet, Clock, HandCoins, Settings2,
  Truck, ShoppingBag,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line,
} from "recharts";
import { useAuth } from "@/contexts/AuthContext";
import useDashboardPrefs from "@/hooks/useDashboardPrefs";
import DashboardCustomizeDialog from "@/components/DashboardCustomizeDialog";
import CashFlowWidget from "@/components/CashFlowWidget";

const LEVEL_STYLES = {
  danger: { border: "border-l-destructive", bg: "bg-destructive/5", icon: AlertOctagon, color: "text-destructive" },
  warning: { border: "border-l-amber-500", bg: "bg-amber-500/5", icon: AlertTriangle, color: "text-amber-600" },
  info: { border: "border-l-sky-500", bg: "bg-sky-500/5", icon: Info, color: "text-sky-600" },
};

function Sparkline({ data, dataKey, color = "#0F766E", height = 40 }) {
  if (!data || data.length === 0) return null;
  return (
    <div style={{ height }} className="w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
          <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} dot={false} isAnimationActive={false} />
          <Tooltip
            cursor={false}
            contentStyle={{ fontSize: 11, padding: "4px 8px" }}
            formatter={(v) => `RD$${Number(v).toLocaleString("es-DO", { maximumFractionDigits: 0 })}`}
            labelFormatter={(l) => `Mes: ${l}`}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function CxcMigrationSection() {
  const [summary, setSummary] = useState(null);
  const [top, setTop] = useState([]);
  const [aging, setAging] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/cxc-migration/summary"),
      api.get("/cxc-migration/top-debtors", { params: { limit: 5 } }),
      api.get("/cxc-migration/aging"),
    ]).then(([s, t, a]) => {
      setSummary(s.data);
      setTop(t.data);
      setAging(a.data);
    }).catch(() => { /* silencioso: puede no haber datos aún */ });
  }, []);

  if (!summary || (summary.clientes_con_saldo_migrado || 0) === 0) return null;
  const bucketMeta = {
    "0-30":  { label: "0 – 30 d",  bg: "bg-emerald-500/10", txt: "text-emerald-700 dark:text-emerald-400" },
    "31-60": { label: "31 – 60 d", bg: "bg-amber-500/10",   txt: "text-amber-700 dark:text-amber-400" },
    "61-90": { label: "61 – 90 d", bg: "bg-orange-500/10",  txt: "text-orange-700 dark:text-orange-400" },
    "+90":   { label: "+90 d",     bg: "bg-red-500/10",     txt: "text-red-700 dark:text-red-400" },
  };

  return (
    <div className="mb-6" data-testid="dashboard-cxc-migration">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="overline">Migración</div>
          <h2 className="text-xl font-bold tracking-tight">CxC históricas (sistema anterior)</h2>
        </div>
        <Link to="/cxc-migration">
          <Button variant="outline" size="sm" data-testid="cxc-migration-goto">Ver módulo</Button>
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <KpiCard
          label="Saldo pendiente migrado"
          value={formatDOP(summary.saldo_pendiente_total)}
          hint={`${summary.clientes_con_saldo_migrado} cliente${summary.clientes_con_saldo_migrado === 1 ? "" : "s"}`}
          icon={Wallet}
          testid="kpi-migrated-pending"
        />
        <KpiCard
          label="Cobrado migrado"
          value={formatDOP(summary.total_cobrado)}
          hint={`Sobre ${formatDOP(summary.saldo_inicial_total)} inicial`}
          icon={HandCoins}
          testid="kpi-migrated-collected"
        />
        {/* Aging quick view */}
        {aging && ["0-30", "31-60"].map((b) => (
          <div key={b} className={`kpi-card ${bucketMeta[b].bg}`} data-testid={`kpi-aging-${b}`}>
            <div className="overline">{bucketMeta[b].label}</div>
            <div className={`text-2xl font-black mt-2 data-cell ${bucketMeta[b].txt}`}>
              {formatDOP(aging.buckets[b])}
            </div>
            <div className="text-xs text-muted-foreground mt-1">{aging.counts[b]} cliente{aging.counts[b] === 1 ? "" : "s"}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top 5 deudores migrados */}
        <div className="bg-card border border-border rounded-md p-4" data-testid="top-migrated-debtors">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold">Top 5 deudores migrados</h3>
            <span className="text-xs text-muted-foreground">Mayor saldo pendiente</span>
          </div>
          {top.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">Sin deudores migrados con saldo.</div>
          ) : (
            <div className="space-y-2">
              {top.map((d, i) => (
                <Link
                  key={d.id}
                  to="/cxc-migration"
                  className="flex items-center justify-between p-2 border border-border/40 rounded hover:bg-accent"
                  data-testid={`migrated-debtor-${i}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="w-6 h-6 rounded-full bg-primary/10 text-primary font-bold text-xs flex items-center justify-center">{i + 1}</span>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold truncate">{d.name}</div>
                      {d.rnc_cedula && <div className="text-[10px] text-muted-foreground data-cell">RNC {d.rnc_cedula}</div>}
                    </div>
                  </div>
                  <div className="data-cell font-bold text-primary shrink-0">{formatDOP(d.saldo_pendiente)}</div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Antigüedad breakdown */}
        {aging && (
          <div className="bg-card border border-border rounded-md p-4" data-testid="aging-breakdown">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold">Antigüedad</h3>
              <Link to="/cxc-migration"><Button size="sm" variant="ghost" className="text-xs">Reporte completo →</Button></Link>
            </div>
            <div className="space-y-2">
              {["0-30", "31-60", "61-90", "+90"].map((b) => {
                const val = aging.buckets[b] || 0;
                const pct = aging.total_pendiente > 0
                  ? Math.round((val / aging.total_pendiente) * 100)
                  : 0;
                return (
                  <div key={b} className="flex items-center gap-3">
                    <span className={`text-xs font-bold w-20 ${bucketMeta[b].txt}`}>{bucketMeta[b].label}</span>
                    <div className="flex-1 h-2 bg-muted rounded overflow-hidden">
                      <div className={`h-full ${bucketMeta[b].bg.replace("/10", "/60")}`} style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-xs data-cell font-bold w-24 text-right">{formatDOP(val)}</span>
                    <span className="text-[10px] text-muted-foreground w-10 text-right">{pct}%</span>
                  </div>
                );
              })}
            </div>
            <div className="mt-3 pt-3 border-t border-border/40 flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Total pendiente</span>
              <span className="text-lg font-black text-primary">{formatDOP(aging.total_pendiente)}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function KpiCard({ label, value, hint, icon: Icon, trend, testid }) {
  const trendUp = trend && trend > 0;
  return (
    <div className="kpi-card" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="overline">{label}</div>
        {Icon && <Icon className="w-4 h-4 text-muted-foreground" />}
      </div>
      <div className="text-3xl font-black tracking-tight mt-3 data-cell">{value}</div>
      {hint && (
        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-2">
          {trend !== undefined && (
            trendUp ? <ArrowUpRight className="w-3 h-3 text-success" /> : <ArrowDownRight className="w-3 h-3 text-destructive" />
          )}
          {hint}
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const [kpis, setKpis] = useState(null);
  const [events, setEvents] = useState([]);
  const [chart, setChart] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [fin, setFin] = useState(null);
  const [pfin, setPfin] = useState(null);   // Iter 47 — purchases/CxP
  const [accMetrics, setAccMetrics] = useState(null);
  const [trends, setTrends] = useState(null);
  const [salesKind, setSalesKind] = useState(() => localStorage.getItem("dashboard.sales_kind") || "net");
  const { user } = useAuth();
  const canSeeAccounting = ["admin", "contador"].includes((user?.role || "").toLowerCase());

  // Iter 46 — Dashboard personalizable
  const { prefs, isVisible, sortSection, save, reset } = useDashboardPrefs();
  const [customizeOpen, setCustomizeOpen] = useState(false);

  const CARD_LABELS = useMemo(() => ({
    "kpi-sales-today": "Ventas hoy",
    "kpi-sales-month": "Ventas del mes",
    "kpi-products": "Productos activos",
    "kpi-customers": "Clientes registrados",
    "fin-utilidad": "Utilidad neta MTD",
    "fin-margen": "Margen bruto",
    "fin-caja": "Posición de caja",
    "fin-itbis": "ITBIS a pagar DGII",
    "cxc-total": "CxC total",
    "cxc-vencidas": "Vencidas",
    "cxc-por-vencer": "Por vencer ≤7d",
    "cxc-cobrado-hoy": "Cobrado hoy",
    "cxp-compras-mes": "Compras del mes",
    "cxp-total": "CxP total",
    "cxp-vencidas": "CxP vencidas >30d",
    "cxp-pagado-hoy": "Pagado hoy a suplidores",
  }), []);
  const CARD_SECTIONS = useMemo(() => ({
    kpis: {
      label: "Row 1 — KPIs operativos",
      ids: ["kpi-sales-today", "kpi-sales-month", "kpi-products", "kpi-customers"],
    },
    financial: {
      label: "Row 2 — Salud financiera del mes (admin/contador)",
      ids: ["fin-utilidad", "fin-margen", "fin-caja", "fin-itbis"],
    },
    cxc: {
      label: "Row 3 — Cuentas por cobrar",
      ids: ["cxc-total", "cxc-vencidas", "cxc-por-vencer", "cxc-cobrado-hoy"],
    },
    cxp: {
      label: "Row 4 — Compras y Cuentas por pagar (admin/contador)",
      ids: ["cxp-compras-mes", "cxp-total", "cxp-vencidas", "cxp-pagado-hoy"],
    },
  }), []);

  const kpisOrder = sortSection(CARD_SECTIONS.kpis.ids);
  const finOrder = sortSection(CARD_SECTIONS.financial.ids);
  const cxcOrder = sortSection(CARD_SECTIONS.cxc.ids);
  const cxpOrder = sortSection(CARD_SECTIONS.cxp.ids);

  useEffect(() => {
    Promise.all([
      api.get(`/dashboard/kpis?sales_kind=${salesKind}`),
      api.get("/dashboard/event-feed"),
      api.get("/dashboard/sales-chart?days=14"),
      api.get("/dashboard/top-products?days=30&limit=5"),
      api.get("/dashboard/financials"),
    ]).then(([k, e, c, t, f]) => {
      setKpis(k.data);
      setEvents(e.data);
      setChart(c.data);
      setTopProducts(t.data);
      setFin(f.data);
    });
    if (canSeeAccounting) {
      api.get("/dashboard/accounting-metrics")
        .then(({ data }) => setAccMetrics(data))
        .catch(() => {});
      api.get("/dashboard/financial-trends?months=6")
        .then(({ data }) => setTrends(data))
        .catch(() => {});
      api.get("/dashboard/purchases-financials")
        .then(({ data }) => setPfin(data))
        .catch(() => {});
    }
  }, [canSeeAccounting, salesKind]);

  useEffect(() => {
    localStorage.setItem("dashboard.sales_kind", salesKind);
  }, [salesKind]);

  const trend = kpis ? (kpis.sales_today - kpis.sales_yesterday) : 0;
  const trendPct = kpis && kpis.sales_yesterday > 0
    ? ((trend / kpis.sales_yesterday) * 100).toFixed(1) : null;

  const salesHint = salesKind === "gross"
    ? "Solo facturas (sin ajustes contables)"
    : "Facturas − Notas de crédito + Notas de débito";

  return (
    <PageContainer>
      <PageHeader
        title="Dashboard"
        subtitle="Resumen operativo en tiempo real — ventas, stock, alertas y eventos críticos."
        testid="dashboard-header"
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setCustomizeOpen(true)}
              data-testid="customize-dashboard-btn"
              title="Ocultar y reordenar tarjetas"
            >
              <Settings2 className="w-4 h-4 mr-1.5" />
              Personalizar
            </Button>
            <Link to="/pos">
              <Button className="bg-primary hover:bg-primary/90 font-bold" data-testid="quick-pos-btn">
                Nueva venta
              </Button>
            </Link>
          </div>
        }
      />

      {/* Toggle Ventas Brutas/Netas — Iter 45.2 */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-muted/30 border border-border rounded-md px-3 py-2 mb-4 text-xs">
        <div className="flex items-center gap-3">
          <span className="overline text-[10px]">Ventas mostradas:</span>
          {[
            { v: "net", label: "Netas (contable)", hint: "Fact. − NC + ND" },
            { v: "gross", label: "Brutas (solo facturas)", hint: "Sin ajustes" },
          ].map((o) => (
            <label key={o.v} className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="radio"
                name="dashboard-sales-kind"
                value={o.v}
                checked={salesKind === o.v}
                onChange={() => setSalesKind(o.v)}
                data-testid={`dashboard-sales-kind-${o.v}`}
                className="accent-primary"
              />
              <span>{o.label}</span>
              <span className="text-muted-foreground text-[10px]">· {o.hint}</span>
            </label>
          ))}
        </div>
        {kpis && salesKind === "net" && Number(kpis.credit_notes_month || 0) > 0 && (
          <div className="text-[10px] text-amber-700 dark:text-amber-400">
            NC del mes: −{formatDOP(kpis.credit_notes_month)}
            {Number(kpis.debit_notes_month || 0) > 0 && <span> · ND: +{formatDOP(kpis.debit_notes_month)}</span>}
          </div>
        )}
      </div>

      {/* Cash Flow del día — vista rápida de ingresos/egresos en efectivo */}
      <div className="mb-6">
        <CashFlowWidget />
      </div>

      {/* KPIs — Iter 46: renderizado dinámico por prefs */}
      {kpisOrder.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="dashboard-row-kpis">
          {kpisOrder.map((id) => {
            if (id === "kpi-sales-today") return (
              <KpiCard
                key={id}
                label={`Ventas hoy${salesKind === "gross" ? " (brutas)" : ""}`}
                value={kpis ? formatDOP(kpis.sales_today) : "—"}
                hint={trendPct ? `${trendPct}% vs ayer · ${salesHint}` : salesHint}
                trend={trend}
                icon={TrendingUp}
                testid="kpi-sales-today"
              />
            );
            if (id === "kpi-sales-month") return (
              <KpiCard
                key={id}
                label={`Ventas del mes${salesKind === "gross" ? " (brutas)" : ""}`}
                value={kpis ? formatDOP(kpis.sales_month) : "—"}
                hint={kpis ? `${kpis.invoices_month} facturas · ${salesHint}` : ""}
                icon={Receipt}
                testid="kpi-sales-month"
              />
            );
            if (id === "kpi-products") return (
              <KpiCard
                key={id}
                label="Productos activos"
                value={kpis ? formatNum(kpis.total_products) : "—"}
                hint={kpis ? `${kpis.low_stock_count} con existencia baja` : ""}
                icon={Package}
                testid="kpi-products"
              />
            );
            if (id === "kpi-customers") return (
              <KpiCard
                key={id}
                label="Clientes registrados"
                value={kpis ? formatNum(kpis.customers_count) : "—"}
                icon={Users}
                testid="kpi-customers"
              />
            );
            return null;
          })}
        </div>
      )}

      {/* KPIs contabilidad (admin/contador) — Iter 46: orden y visibilidad configurables */}
      {canSeeAccounting && accMetrics && finOrder.length > 0 && (
        <div className="mb-6" data-testid="dashboard-accounting-metrics">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="overline">Contabilidad · {accMetrics.period}</div>
              <h2 className="text-xl font-bold tracking-tight">Salud financiera del mes</h2>
            </div>
            <Link to="/accounting">
              <Button variant="outline" size="sm" data-testid="quick-accounting-btn">
                Ver contabilidad
              </Button>
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {finOrder.map((id) => {
              if (id === "fin-utilidad") return (
                <Link key={id} to="/accounting" className="kpi-card" data-testid="kpi-net-income">
                  <div className="flex items-start justify-between">
                    <div className="overline">Utilidad neta MTD</div>
                    <TrendingUp className={`w-4 h-4 ${accMetrics.current.utilidad_neta >= 0 ? "text-success" : "text-destructive"}`} />
                  </div>
                  <div className={`text-3xl font-black tracking-tight mt-3 data-cell ${accMetrics.current.utilidad_neta >= 0 ? "text-success" : "text-destructive"}`}>
                    {formatDOP(accMetrics.current.utilidad_neta)}
                  </div>
                  <div className="text-xs text-muted-foreground mt-2">
                    {accMetrics.variation.utilidad_pct !== null
                      ? `${accMetrics.variation.utilidad_pct > 0 ? "+" : ""}${accMetrics.variation.utilidad_pct}% vs mes anterior`
                      : "Sin datos previos"}
                  </div>
                  {trends?.points?.length > 0 && (
                    <div className="mt-2" data-testid="sparkline-utilidad">
                      <Sparkline data={trends.points} dataKey="utilidad_neta" color="#16A34A" height={36} />
                      <div className="text-[10px] text-muted-foreground mt-1">Últimos 6 meses</div>
                    </div>
                  )}
                </Link>
              );
              if (id === "fin-margen") return (
                <div key={id} className="kpi-card" data-testid="kpi-gross-margin">
                  <div className="flex items-start justify-between">
                    <div className="overline">Margen bruto</div>
                    <TrendingUp className="w-4 h-4 text-primary" />
                  </div>
                  <div className="text-3xl font-black tracking-tight mt-3 data-cell text-primary">
                    {accMetrics.current.margen_bruto_pct.toFixed(1)}%
                  </div>
                  <div className="text-xs text-muted-foreground mt-2">
                    Neto: {accMetrics.current.margen_neto_pct.toFixed(1)}%
                  </div>
                </div>
              );
              if (id === "fin-caja") return (
                <Link key={id} to="/accounting" className="kpi-card" data-testid="kpi-cash-position">
                  <div className="flex items-start justify-between">
                    <div className="overline">Posición de caja</div>
                    <Wallet className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div className="text-3xl font-black tracking-tight mt-3 data-cell text-emerald-700 dark:text-emerald-400">
                    {formatDOP(accMetrics.cash_position.total)}
                  </div>
                  <div className="text-xs text-muted-foreground mt-2">
                    Caja + Bancos
                  </div>
                  {trends?.points?.length > 0 && (
                    <div className="mt-2" data-testid="sparkline-cash">
                      <Sparkline data={trends.points} dataKey="cash_position" color="#0F766E" height={36} />
                      <div className="text-[10px] text-muted-foreground mt-1">Últimos 6 meses</div>
                    </div>
                  )}
                </Link>
              );
              if (id === "fin-itbis") return (
                <div key={id} className={`kpi-card ${accMetrics.itbis.a_pagar > 0 ? "border-l-4 border-l-amber-500" : ""}`} data-testid="kpi-itbis-net">
                  <div className="flex items-start justify-between">
                    <div className="overline">ITBIS a pagar DGII</div>
                    <Receipt className="w-4 h-4 text-amber-600" />
                  </div>
                  <div className={`text-3xl font-black tracking-tight mt-3 data-cell ${accMetrics.itbis.a_pagar > 0 ? "text-amber-700 dark:text-amber-400" : "text-success"}`}>
                    {formatDOP(accMetrics.itbis.a_pagar)}
                  </div>
                  <div className="text-xs text-muted-foreground mt-2">
                    Repercutido {formatDOP(accMetrics.itbis.repercutido)} · Soportado {formatDOP(accMetrics.itbis.soportado)}
                  </div>
                </div>
              );
              return null;
            })}
          </div>
        </div>
      )}

      {/* Sección Finanzas — CxC, vencidas, cobros del día */}
      {fin && (
        <div className="mb-6" data-testid="dashboard-financials">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="overline">Finanzas</div>
              <h2 className="text-xl font-bold tracking-tight">Cuentas por cobrar</h2>
            </div>
            <div className="flex gap-2">
              <Link to="/receivables-aging">
                <Button variant="outline" size="sm" data-testid="quick-aging-btn">
                  Reporte de antigüedad
                </Button>
              </Link>
              <Link to="/customer-statements">
                <Button variant="outline" size="sm" data-testid="quick-statement-btn">
                  Estados de cuenta
                </Button>
              </Link>
              <Link to="/receipts">
                <Button variant="outline" size="sm" data-testid="quick-receipts-btn">
                  Recibos
                </Button>
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {cxcOrder.map((id) => {
              if (id === "cxc-total") return (
                <Link key={id} to="/receivables-aging" data-testid="kpi-cxc-total-link">
                  <KpiCard
                    label="CxC total"
                    value={formatDOP(fin.total_receivable)}
                    hint={`${fin.invoice_count_open} facturas abiertas`}
                    icon={Wallet}
                    testid="kpi-cxc-total"
                  />
                </Link>
              );
              if (id === "cxc-vencidas") return (
                <Link key={id} to="/receivables-aging?filter=fuera_plazo" className={`kpi-card ${fin.overdue.count > 0 ? "border-l-4 border-l-destructive" : ""}`} data-testid="kpi-overdue">
                  <div className="flex items-start justify-between">
                    <div className="overline">Vencidas</div>
                    <AlertOctagon className={`w-4 h-4 ${fin.overdue.count > 0 ? "text-destructive" : "text-muted-foreground"}`} />
                  </div>
                  <div className={`text-3xl font-black tracking-tight mt-3 data-cell ${fin.overdue.count > 0 ? "text-destructive" : ""}`}>
                    {formatDOP(fin.overdue.total)}
                  </div>
                  <div className="text-xs text-muted-foreground mt-2">
                    {fin.overdue.count} facturas
                  </div>
                </Link>
              );
              if (id === "cxc-por-vencer") return (
                <Link key={id} to="/receivables-aging?filter=vencida_gracia" className="kpi-card" data-testid="kpi-due-soon">
                  <div className="flex items-start justify-between">
                    <div className="overline">Por vencer ≤7d</div>
                    <Clock className="w-4 h-4 text-amber-500" />
                  </div>
                  <div className="text-3xl font-black tracking-tight mt-3 data-cell text-amber-600">
                    {formatDOP(fin.due_soon.total)}
                  </div>
                  <div className="text-xs text-muted-foreground mt-2">{fin.due_soon.count} facturas</div>
                </Link>
              );
              if (id === "cxc-cobrado-hoy") return (
                <KpiCard
                  key={id}
                  label="Cobrado hoy"
                  value={formatDOP(fin.collected_today.total)}
                  hint={`${fin.collected_today.receipts_count + fin.collected_today.payments_count} recibos`}
                  icon={HandCoins}
                  testid="kpi-collected-today"
                />
              );
              return null;
            })}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Top deudores */}
            <div className="bg-card border border-border rounded-md p-4" data-testid="top-debtors">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold">Top 5 deudores</h3>
                <span className="text-xs text-muted-foreground">Saldo total pendiente</span>
              </div>
              {fin.top_debtors.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground">Sin cuentas por cobrar.</div>
              ) : (
                <div className="space-y-2">
                  {fin.top_debtors.map((d, i) => (
                    <Link
                      key={d.customer_id + i}
                      to={`/receivables-aging/${d.customer_id}`}
                      className="flex items-center justify-between p-2 border border-border/40 rounded hover:bg-accent"
                      data-testid={`debtor-${i}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="w-6 h-6 rounded-full bg-primary/10 text-primary font-bold text-xs flex items-center justify-center">{i + 1}</span>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold truncate">{d.customer_name}</div>
                          <div className="text-[10px] text-muted-foreground">{d.invoice_count} factura{d.invoice_count !== 1 ? "s" : ""}</div>
                        </div>
                      </div>
                      <div className="data-cell font-bold text-primary shrink-0">{formatDOP(d.balance)}</div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Vencidas + por vencer */}
            <div className="bg-card border border-border rounded-md p-4" data-testid="upcoming-invoices">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold">Facturas críticas</h3>
                <span className="text-xs text-muted-foreground">Vencidas y por vencer</span>
              </div>
              {(fin.overdue.items.length + fin.due_soon.items.length) === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground">🎉 Sin facturas vencidas ni próximas a vencer.</div>
              ) : (
                <div className="space-y-1 max-h-[280px] overflow-y-auto">
                  {fin.overdue.items.map((f) => (
                    <div key={"o-" + f.number} className="flex items-center justify-between p-2 border-l-2 border-l-destructive bg-destructive/5 rounded text-sm">
                      <div className="min-w-0">
                        <div className="data-cell font-bold">{f.number} · {f.customer_name}</div>
                        <div className="text-[10px] text-destructive">Venció hace {Math.abs(f.days_offset)} día(s)</div>
                      </div>
                      <div className="data-cell font-bold text-destructive shrink-0">{formatDOP(f.balance_due)}</div>
                    </div>
                  ))}
                  {fin.due_soon.items.map((f) => (
                    <div key={"d-" + f.number} className="flex items-center justify-between p-2 border-l-2 border-l-amber-500 bg-amber-500/5 rounded text-sm">
                      <div className="min-w-0">
                        <div className="data-cell font-bold">{f.number} · {f.customer_name}</div>
                        <div className="text-[10px] text-amber-600">Vence en {f.days_offset} día(s)</div>
                      </div>
                      <div className="data-cell font-bold text-amber-700 shrink-0">{formatDOP(f.balance_due)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* CxC Migración — solo si existen saldos migrados */}
      <CxcMigrationSection />

      {/* Iter 47 — Sección Compras & CxP (admin/contador) */}
      {canSeeAccounting && pfin && cxpOrder.length > 0 && (
        <div className="mb-6" data-testid="dashboard-purchases">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="overline">Compras & CxP</div>
              <h2 className="text-xl font-bold tracking-tight">Movimientos con suplidores</h2>
            </div>
            <div className="flex gap-2">
              <Link to="/purchases">
                <Button variant="outline" size="sm" data-testid="quick-purchases-btn">
                  Ver compras
                </Button>
              </Link>
              <Link to="/accounting">
                <Button variant="outline" size="sm" data-testid="quick-payables-btn">
                  Cuentas por pagar
                </Button>
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {cxpOrder.map((id) => {
              if (id === "cxp-compras-mes") return (
                <Link key={id} to="/purchases" data-testid="kpi-cxp-compras-mes-link">
                  <KpiCard
                    label="Compras del mes"
                    value={formatDOP(pfin.purchases_month)}
                    hint={`${pfin.purchases_month_count} facturas · Hoy: ${formatDOP(pfin.purchases_today)}`}
                    icon={ShoppingBag}
                    testid="kpi-cxp-compras-mes"
                  />
                </Link>
              );
              if (id === "cxp-total") return (
                <Link key={id} to="/accounting" data-testid="kpi-cxp-total-link">
                  <KpiCard
                    label="CxP total"
                    value={formatDOP(pfin.total_payable)}
                    hint={`${pfin.purchases_open_count} facturas abiertas`}
                    icon={Wallet}
                    testid="kpi-cxp-total"
                  />
                </Link>
              );
              if (id === "cxp-vencidas") return (
                <Link
                  key={id}
                  to="/accounting"
                  className={`kpi-card ${pfin.overdue_payable.count > 0 ? "border-l-4 border-l-destructive" : ""}`}
                  data-testid="kpi-cxp-vencidas"
                >
                  <div className="flex items-start justify-between">
                    <div className="overline">CxP vencidas &gt;30d</div>
                    <AlertOctagon className={`w-4 h-4 ${pfin.overdue_payable.count > 0 ? "text-destructive" : "text-muted-foreground"}`} />
                  </div>
                  <div className={`text-3xl font-black tracking-tight mt-3 data-cell ${pfin.overdue_payable.count > 0 ? "text-destructive" : ""}`}>
                    {formatDOP(pfin.overdue_payable.total)}
                  </div>
                  <div className="text-xs text-muted-foreground mt-2">
                    {pfin.overdue_payable.count} facturas
                  </div>
                </Link>
              );
              if (id === "cxp-pagado-hoy") return (
                <KpiCard
                  key={id}
                  label="Pagado hoy a suplidores"
                  value={formatDOP(pfin.paid_today_suppliers.total)}
                  hint={`${pfin.paid_today_suppliers.receipts_count + pfin.paid_today_suppliers.payments_count} pagos`}
                  icon={HandCoins}
                  testid="kpi-cxp-pagado-hoy"
                />
              );
              return null;
            })}
          </div>

          {/* Top 5 suplidores */}
          <div className="bg-card border border-border rounded-md p-4" data-testid="top-suppliers">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold flex items-center gap-2">
                <Truck className="w-4 h-4 text-primary" />
                Top 5 suplidores
              </h3>
              <span className="text-xs text-muted-foreground">Saldo total pendiente</span>
            </div>
            {pfin.top_suppliers.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">Sin cuentas por pagar.</div>
            ) : (
              <div className="space-y-2">
                {pfin.top_suppliers.map((s, idx) => (
                  <Link
                    key={s.supplier_id + idx}
                    to={`/accounting`}
                    className="flex items-center justify-between p-2 rounded hover:bg-accent border border-border/40 transition-colors"
                    data-testid={`top-supplier-${idx}`}
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0">
                        {idx + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-sm truncate">{s.supplier_name}</div>
                        <div className="text-[11px] text-muted-foreground data-cell">
                          {s.supplier_rnc || "—"} · {s.purchase_count} factura{s.purchase_count === 1 ? "" : "s"}
                        </div>
                      </div>
                    </div>
                    <div className="data-cell font-bold text-destructive shrink-0">
                      {formatDOP(s.balance)}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main grid: chart + event feed */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Chart */}
        <div className="lg:col-span-2 bg-card border border-border rounded-md p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline">Últimos 14 días</div>
              <h2 className="text-xl font-bold tracking-tight">Ventas por día</h2>
            </div>
          </div>
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chart} margin={{ top: 10, right: 0, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#EA580C" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#EA580C" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#CBD5E1" opacity={0.4} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} tickFormatter={(d) => d?.slice(5)} />
                <YAxis tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} />
                <Tooltip
                  contentStyle={{ background: "#0F172A", color: "#fff", border: "none", borderRadius: 6, fontSize: 12 }}
                  formatter={(v) => formatDOP(v)}
                />
                <Area type="monotone" dataKey="total" stroke="#EA580C" fill="url(#grad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Event Feed */}
        <div className="bg-card border border-border rounded-md p-5" data-testid="event-feed">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline">Feed de eventos</div>
              <h2 className="text-xl font-bold tracking-tight">Alertas activas</h2>
            </div>
            <span className="text-xs text-muted-foreground data-cell">{events.length}</span>
          </div>
          <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
            {events.length === 0 && (
              <div className="text-sm text-muted-foreground py-8 text-center">
                Todo en orden ✓
              </div>
            )}
            {events.map((e, i) => {
              const style = LEVEL_STYLES[e.level] || LEVEL_STYLES.info;
              const Icon = style.icon;
              return (
                <div
                  key={i}
                  className={`border-l-2 ${style.border} ${style.bg} pl-3 py-2 pr-2 rounded-r-md`}
                  data-testid={`event-${e.category}`}
                >
                  <div className="flex items-start gap-2">
                    <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${style.color}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-bold uppercase tracking-wider">{e.title}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{e.message}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Top Products */}
      <div className="mt-4 bg-card border border-border rounded-md p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="overline">Últimos 30 días</div>
            <h2 className="text-xl font-bold tracking-tight">Productos más vendidos</h2>
          </div>
        </div>
        {topProducts.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">Sin ventas registradas todavía.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="overline border-b border-border">
                <th className="text-left py-2 font-bold">Producto</th>
                <th className="text-left py-2 font-bold">SKU</th>
                <th className="text-right py-2 font-bold">Cantidad</th>
                <th className="text-right py-2 font-bold">Ingreso</th>
              </tr>
            </thead>
            <tbody>
              {topProducts.map((p, i) => (
                <tr key={i} className="border-b border-border/60 hover:bg-accent">
                  <td className="py-2.5">{p.name}</td>
                  <td className="py-2.5 data-cell text-xs text-muted-foreground">{p.sku}</td>
                  <td className="py-2.5 text-right data-cell">{formatNum(p.quantity)}</td>
                  <td className="py-2.5 text-right data-cell font-semibold">{formatDOP(p.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Iter 46 — diálogo de personalización */}
      <DashboardCustomizeDialog
        open={customizeOpen}
        onOpenChange={setCustomizeOpen}
        prefs={prefs}
        labels={CARD_LABELS}
        sections={CARD_SECTIONS}
        onSave={save}
        onReset={reset}
      />
    </PageContainer>
  );
}
